import React, { useEffect, useState } from 'react';
import { getAutosaveTimestamp } from '../../services/autosave.service';

interface AutosaveNotificationProps {
  onClose: () => void;
}

const AutosaveNotification: React.FC<AutosaveNotificationProps> = ({ onClose }) => {
  const [visible, setVisible] = useState(false);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const timestamp = getAutosaveTimestamp();
    
    if (timestamp) {
      const now = Date.now();
      const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
      
      let timeDisplay = '';
      if (diffInMinutes < 1) {
        timeDisplay = 'just now';
      } else if (diffInMinutes === 1) {
        timeDisplay = '1 minute ago';
      } else if (diffInMinutes < 60) {
        timeDisplay = `${diffInMinutes} minutes ago`;
      } else {
        const hours = Math.floor(diffInMinutes / 60);
        timeDisplay = hours === 1 ? '1 hour ago' : `${hours} hours ago`;
      }
      
      setTimeAgo(timeDisplay);
      setVisible(true);
      
      // Auto-hide after 3 seconds
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onClose, 300); // Allow fade-out animation to complete
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [onClose]);

  if (!visible) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 bg-white border border-gray-200 shadow-lg rounded-md p-4 max-w-md transition-opacity duration-300"
      style={{ opacity: visible ? 1 : 0, zIndex: 1000 }}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0 text-green-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-gray-900">Design Restored</h3>
          <div className="mt-1 text-sm text-gray-500">
            Your previously unsaved design from {timeAgo} has been restored.
          </div>
        </div>
        <button
          type="button"
          className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-500"
          onClick={() => {
            setVisible(false);
            setTimeout(onClose, 300);
          }}
        >
          <span className="sr-only">Close</span>
          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AutosaveNotification; 